import React, { useEffect, useMemo, useState } from 'react';
import Header from '@/components/Header';
import HeroSection from '@/components/HeroSection';
import BannerCarousel from '@/components/BannerCarousel';
import ExploreEvents from '@/components/home/ExploreEvents';
import VenuesNearYou from '@/components/home/VenuesNearYou';
import ArtistsRail from '@/components/home/ArtistsRail';
import AllEvents from '@/components/home/AllEvents';
import PastEvents from '@/components/home/PastEvents';
import Footer from '@/components/Footer';
import { useCityEvents } from '@/hooks/useCatalog';
import { useEvents } from '@/context/EventContext';
import { isUpcoming } from '@/lib/filters';
import PromoBand from '@/components/home/PromoBand';
import axiosPublic from '@/api/axiosPublic';

/** The layout used when the admin hasn't configured a website home. */
const DEFAULT_LAYOUT = ['web_hero', 'banners_carousel', 'explore_tiles', 'venues_grid', 'artists_rail', 'events_grid', 'past_events'];

/**
 * Website home sections from LK Admin → Marketing → Website Home
 * (GET /home/sections?platform=web). null = not loaded / failed → default layout.
 */
function useWebHomeSections() {
  const [sections, setSections] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axiosPublic
      .get('/home/sections', { params: { platform: 'web' } })
      .then((res) => {
        const rows = res.data?.data;
        if (!cancelled && Array.isArray(rows) && rows.length) setSections(rows);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  return sections;
}

/**
 * The storefront. Events are fetched once here and handed down, so the hero,
 * the venue rail and the grid all read the same list.
 */
const HomePage = () => {
  // Scoped to the visitor's city; a city with nothing of its own yet shows the
  // cities it borrows from (LK Admin → Zones → "What customers see here").
  const { events, loading, city, ownUpcomingCount, borrowedFrom } = useCityEvents();
  const { searchQuery } = useEvents();
  const [types, setTypes] = useState([]);

  const upcoming = useMemo(() => events.filter(isUpcoming), [events]);

  const notice =
    loading || !city || borrowedFrom.length === 0
      ? null
      : ownUpcomingCount === 0
        ? `No events in ${city.name} yet — here's what's on in ${borrowedFrom.join(' & ')}.`
        : `Events in ${city.name} first, then what's on in ${borrowedFrom.join(' & ')}.`;

  const sections = useWebHomeSections();
  const layout = sections ?? DEFAULT_LAYOUT.map((type) => ({ id: type, type, config: {} }));

  const render = (section) => {
    switch (section.type) {
      case 'web_hero':
        return <HeroSection key={section.id} events={upcoming} loading={loading} />;
      case 'banners_carousel':
        return <BannerCarousel key={section.id} />;
      case 'explore_tiles':
        return <ExploreEvents key={section.id} />;
      case 'venues_grid':
        return <VenuesNearYou key={section.id} />;
      case 'artists_rail':
        return <ArtistsRail key={section.id} title={section.title || section.config?.title || undefined} />;
      case 'events_grid':
        return (
          <AllEvents
            key={section.id}
            events={events}
            loading={loading}
            query={searchQuery}
            types={types}
            onTypesChange={setTypes}
            notice={notice}
          />
        );
      case 'past_events':
        return <PastEvents key={section.id} />;
      case 'promo_banner':
      case 'offers_band':
        return <PromoBand key={section.id} section={section} />;
      default:
        // Types without a website renderer yet (e.g. spotlight_services) are skipped.
        return null;
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-surface-muted">
      <Header />
      <main className="flex-grow">{layout.map(render)}</main>
      <Footer />
    </div>
  );
};

export default HomePage;
